// ═══ SAVINGS FROM THE BILLS THEMSELVES, TO THE CENT (Philip, 2026-10-09) ═══
//
// The county rounds each tax line to the cent, per bill, then sums — so a saving measured as reduction × rate is
// off by a cent or two from what the client was actually billed. 30-3129-029-0010 / 2025: SigerTax and the bills
// say $44,346.88 (original ad valorem $467,639.38 − revised $423,292.50); reduction × rate said $44,346.89.
//
//   B  'bills' — the year has a bill on the BASELINE values (the original) AND one on the FINAL values (the
//      revised): savings = Σ the original's ad valorem lines − Σ the revised's, as printed. This is the only
//      method that is exact whatever rate precision the county prints: 2024's School Board Operating prints
//      5.46800 and bills 136,622.09, where 24,985,750 × 5.468 / 1000 = 136,622.081 — its real rate has more
//      decimals than the paper shows, so no recomputation from the printed rate can reach the billed cent.
//   A  'lines' — only one of those bills: savings = Σ over its lines of round(baseline × rate, 2) −
//      round(final × rate, 2), each line paired to its base by the taxable value it was billed on.
//   null — neither applies (no bill this year, lines that cannot be placed on a base, a unit that cannot be
//      told): the caller keeps its existing method.
//
// ⛔ WHICH VALUES A BILL IS ON IS READ FROM ITS LINES, never from its status or its order. A bill is on a set of
// values when its rated lines' taxable values are exactly that set. A revised bill SUPERSEDES the original when it
// is scanned (superseded_at), but the original stays in tax_bills and is read here — that is the point.
// A REJECTED bill is a bill someone said is wrong; it is never used. Nor is a SUPPLEMENTAL: its lines are prorated, on its
// own fiscal year's rate (Philip, 2026-10-29).
//
// PRE-APPEAL REFERENCES (tax_bill_references) count as bills HERE AND NOWHERE ELSE. Originals are usually filed as
// documents without scanning; scanning one as a reference stores its lines for this calculation only, so it can
// never become a live bill, be transmitted or reach Approvals. The caller passes them separately from `bills` so
// nothing else (this year's rate, the estimate mark) can mistake one for this year's bill.
//
// An ENTERED reference (source 'entered') is the original's printed total ad valorem typed by hand — no document, no
// lines. With a bill on the final values it gives method 'entered': entered original − revised, as printed. A scanned
// original matched by its lines wins over it, being read rather than typed.
//
// pairs: [{ baseline, final }] — one per taxable base, the taxable totals the saving is measured between.
import { splitJurisdictionLines, classifyLine, resolveRateUnit, isSupplementalBill } from '../reports/taxRate';
import { moneyNum } from './money';
import { TAX_BILL_STATUS } from '../constants/statuses';

const cents = (n) => Math.round((n + Number.EPSILON) * 100);
const r2 = (n) => cents(n) / 100;

function billView(bill) {
  const lines = Array.isArray(bill?.jurisdiction_rates) ? bill.jurisdiction_rates : [];
  const { rated, sumRatedAmount } = splitJurisdictionLines(lines);
  if (!rated.length || sumRatedAmount == null) return null;
  const bases = rated.map((l) => moneyNum(l.taxable_value));
  if (bases.some((b) => b == null || b === 0)) return null;         // a line with no base cannot be placed
  return { bill, rated, adValorem: sumRatedAmount, bases: new Set(bases.map((b) => Math.round(b))) };
}

const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

export function billSavings({ bills, references, pairs }) {
  const ps = (pairs || []).filter((p) => p && p.baseline != null && p.final != null);
  if (!ps.length || ps.length !== (pairs || []).length) return null;
  const baselineSet = new Set(ps.map((p) => Math.round(p.baseline)));
  const finalSet = new Set(ps.map((p) => Math.round(p.final)));
  if (sameSet(baselineSet, finalSet)) return null;                   // nothing came off: no bill tells the two apart

  const views = [...(bills || []).filter((b) => b && b.status !== TAX_BILL_STATUS.REJECTED && !isSupplementalBill(b)), ...(references || [])]
    .map(billView).filter(Boolean);
  const onBaseline = views.filter((v) => sameSet(v.bases, baselineSet));
  const onFinal = views.filter((v) => sameSet(v.bases, finalSet));
  // Instalments of one bill each carry its annual lines; they must agree, or which one is "the bill" is a guess.
  const agreed = (vs) => (vs.length && vs.every((v) => cents(v.adValorem) === cents(vs[0].adValorem)) ? vs[0] : null);
  const orig = agreed(onBaseline);
  const rev = agreed(onFinal);

  if (orig && rev) {
    return { method: 'bills', total: r2(orig.adValorem - rev.adValorem), baselineAdValorem: r2(orig.adValorem), finalAdValorem: r2(rev.adValorem) };
  }
  const entered = (references || []).find((r) => r && r.source === 'entered' && moneyNum(r.total_ad_valorem) > 0);
  if (entered && rev) {
    const typed = moneyNum(entered.total_ad_valorem);
    return { method: 'entered', total: r2(typed - rev.adValorem), baselineAdValorem: r2(typed), finalAdValorem: r2(rev.adValorem) };
  }

  // A — per-line rounding on the one bill there is, the final's preferred.
  const one = rev || orig;
  if (!one) return null;
  const side = one === rev ? 'final' : 'baseline';
  const unit = resolveRateUnit(one.rated);
  if (!unit.confident) return null;
  let total = 0;
  for (const l of one.rated) {
    const base = Math.round(moneyNum(l.taxable_value));
    // The line's base must name ONE pair. Two bases billed at the same value (25,000,000 to County and to School
    // on 30-3129-029-0010 / 2025 revised) with different baselines leave the line unplaceable.
    const other = new Set(ps.filter((p) => Math.round(p[side]) === base).map((p) => Math.round(side === 'final' ? p.baseline : p.final)));
    if (other.size !== 1) return null;
    const [o] = other;
    // A rate RECOVERED from amount ÷ taxable is in percent whatever the bill's unit, and is the bill's own
    // arithmetic restated — not a printed rate to round against. Not placeable.
    const c = classifyLine(l);
    if (c.recovered) return null;
    const rate = c.rate;
    const b = side === 'final' ? o : base;
    const f = side === 'final' ? base : o;
    total += cents(b * rate / unit.divisor) - cents(f * rate / unit.divisor);
  }
  return { method: 'lines', total: total / 100, lineCount: one.rated.length, side };
}
